import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FlaskConical, FileText, Loader2, AlertTriangle, Lock, Plus, Trash2, Check, X, Pencil } from 'lucide-react';
import CpscTable, { CPSC_TH_CLASS } from './CpscTable';

/**
 * CpscMappingsViewer — CHA-169 (read) + CHA-202 (lab CRUD). GLOBAL CPSC lookup tables:
 *   cpsc_lab_mappings      (BV Lab Location → CPSC 4-digit Lab ID)  — add/edit/delete here
 *   cpsc_citation_mappings (recognized standard → CPSC citation code) — read-only (seed-managed)
 *
 * Reads straight from Firestore; every write goes through the audited `manageCpscMapping` callable
 * (server-side validation: the CPSC Lab ID must be 4 digits). Admin-gated (admin | platform_owner).
 */
const s = (v) => String(v ?? '').trim();

// Mapping table. When `manage` (the CRUD callable wrapper) is passed, rows are editable + a bottom add-row
// appears; otherwise it's read-only (the citation table stays read-only in v1).
function MappingTable({ title, icon: Icon, rows, keyCol, valCol, keyField, valField, empty, manage, keyPlaceholder, valPlaceholder, valHint }) {
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [addKey, setAddKey] = useState('');
  const [addVal, setAddVal] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <div className="bg-carbon-white border border-carbon-border overflow-hidden">
      <div className="px-4 py-2 bg-carbon-gray-10 border-b border-carbon-border flex items-center gap-2">
        <Icon size={15} className="text-carbon-gray-70" />
        <span className="text-sm font-medium text-carbon-ink">{title}</span>
        <span className="font-plex-mono text-[10.5px] text-carbon-gray-50">({rows.length})</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className={`${CPSC_TH_CLASS} text-left`}>
            <th className="px-4 py-2 font-normal">{keyCol}</th>
            <th className="px-4 py-2 font-normal">{valCol}</th>
            {manage && <th className="px-4 py-2 font-normal w-20" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={manage ? 3 : 2} className="px-4 py-6 text-center text-sm text-carbon-gray-50">{empty}</td></tr>}
          {rows.map((r) => {
            const k = s(r[keyField]) || r.id;
            return (
              <tr key={r.id} className="border-b border-carbon-gray-10 last:border-0">
                <td className="px-4 py-2 text-carbon-gray-80">{k}</td>
                <td className="px-4 py-2 font-plex-mono text-carbon-ink">
                  {manage && editId === r.id
                    ? <input value={editVal} onChange={(e) => setEditVal(e.target.value)} placeholder={valPlaceholder} className="border border-carbon-gray-30 bg-carbon-gray-10 px-1.5 py-0.5 w-24 font-plex-mono focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" />
                    : (s(r[valField]) || '—')}
                </td>
                {manage && (
                  <td className="px-4 py-2">
                    {editId === r.id ? (
                      <div className="flex items-center gap-1">
                        <button disabled={busy} onClick={() => run(async () => { await manage('update', k, s(editVal)); setEditId(null); })} className="p-1 text-carbon-green-50 hover:bg-carbon-green-10 disabled:opacity-40" title="Save"><Check size={14} /></button>
                        <button disabled={busy} onClick={() => setEditId(null)} className="p-1 text-carbon-gray-40 hover:bg-carbon-gray-10" title="Cancel"><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button disabled={busy} onClick={() => { setEditId(r.id); setEditVal(s(r[valField])); }} className="p-1 text-carbon-gray-70 hover:bg-carbon-gray-10" title="Edit"><Pencil size={13} /></button>
                        <button disabled={busy} onClick={() => run(async () => { if (window.confirm(`Delete lab mapping "${k}"?`)) await manage('delete', k); })} className="p-1 text-carbon-red-60 hover:bg-carbon-gray-10" title="Delete"><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {manage && (
            <tr className="bg-carbon-gray-10/60">
              <td className="px-4 py-2"><input value={addKey} onChange={(e) => setAddKey(e.target.value)} placeholder={keyPlaceholder} className="border border-carbon-gray-30 bg-carbon-white px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" /></td>
              <td className="px-4 py-2"><input value={addVal} onChange={(e) => setAddVal(e.target.value)} placeholder={valPlaceholder} className="border border-carbon-gray-30 bg-carbon-white px-1.5 py-0.5 w-24 font-plex-mono focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" /></td>
              <td className="px-4 py-2">
                <button disabled={busy || !s(addKey) || !s(addVal)} onClick={() => run(async () => { await manage('create', s(addKey), s(addVal)); setAddKey(''); setAddVal(''); })} className="flex items-center gap-1 px-2 py-1 text-xs bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-40"><Plus size={12} /> Add</button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {manage && valHint && <div className="px-4 py-1.5 text-[11px] text-carbon-gray-50 border-t border-carbon-gray-10">{valHint}</div>}
    </div>
  );
}

// CHA-203: per-client BV field templates — which report column each field comes from, per retailer. v1 seeds
// Walmart (→ Product ID from SKU). No template for a client → the ingest uses the default Style No. heuristic.
function ClientTemplates({ rows, manage }) {
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [aliases, setAliases] = useState('');
  const [productId, setProductId] = useState('SKU');
  const blank = { styleNo: 'Style No.', color: 'Color', manufactureDate: 'CPSIA Date of Manufacture' };
  const add = async () => {
    if (!label.trim() || !aliases.trim() || !productId.trim() || busy) return;
    setBusy(true);
    try {
      await manage('create', { template: { label: label.trim(), aliases: aliases.split(',').map((a) => a.trim()).filter(Boolean), fields: { productId: productId.trim(), ...blank }, priority: 1 } });
      setLabel(''); setAliases('');
    } catch { /* error surfaced by manage */ } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm(`Delete template "${id}"? New uploads for its clients fall back to the default heuristic.`)) return; setBusy(true); try { await manage('delete', { id }); } catch { /* surfaced */ } finally { setBusy(false); } };
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-carbon-ink flex items-center gap-1.5"><FileText size={14} /> Client Field Templates</h4>
      <p className="text-xs text-carbon-gray-70">Per retailer, which BV column holds the Product ID. Auto-matched by the report's <strong>Client Name</strong> (substring). No template → default Style No.</p>
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <CpscTable
          headClassName={`${CPSC_TH_CLASS} text-left`}
          columns={[
            { label: 'Label', className: 'px-3 py-2' },
            { label: 'Client Name aliases', className: 'px-3 py-2' },
            { label: 'Product ID column', className: 'px-3 py-2' },
            { label: '', className: 'px-3 py-2 w-10' },
          ]}
        >
            {!rows.length && <tr><td colSpan={4} className="px-3 py-3 text-carbon-gray-50 text-xs">No templates — the default Style No. heuristic is used for every client.</td></tr>}
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-carbon-gray-10">
                <td className="px-3 py-2 font-medium text-carbon-ink">{s(t.label)}</td>
                <td className="px-3 py-2 text-carbon-gray-70 text-xs">{(t.aliases || []).join(', ')}</td>
                <td className="px-3 py-2 font-plex-mono text-xs">{s(t.fields?.productId)}</td>
                <td className="px-3 py-2"><button onClick={() => del(t.id)} disabled={busy} className="text-carbon-red-60 hover:opacity-80 disabled:opacity-50" title="Delete"><Trash2 size={13} /></button></td>
              </tr>
            ))}
            <tr className="border-t border-carbon-border bg-carbon-gray-10">
              <td className="px-3 py-2"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Walmart" className="w-full text-xs border border-carbon-gray-30 bg-carbon-white px-1.5 py-1" aria-label="template label" /></td>
              <td className="px-3 py-2"><input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="WALMART, WAL-MART" className="w-full text-xs border border-carbon-gray-30 bg-carbon-white px-1.5 py-1" aria-label="template aliases" /></td>
              <td className="px-3 py-2"><input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="SKU" className="w-full text-xs border border-carbon-gray-30 bg-carbon-white px-1.5 py-1" aria-label="template product id column" /></td>
              <td className="px-3 py-2"><button onClick={add} disabled={busy || !label.trim() || !aliases.trim()} className="text-carbon-green-50 hover:opacity-80 disabled:opacity-40" title="Add"><Plus size={14} /></button></td>
            </tr>
        </CpscTable>
      </div>
    </div>
  );
}

export default function CpscMappingsViewer({ userProfile } = {}) {
  const role = userProfile?.role;
  const isAdmin = role === 'platform_owner' || role === 'admin';

  const tenantId = userProfile?.tenantId || 'inmocean';
  const [labs, setLabs] = useState([]);
  const [citations, setCitations] = useState([]);
  const [templates, setTemplates] = useState([]); // CHA-203 per-client field templates
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const db = getFirestore();
      const [labSnap, citeSnap, tplSnap] = await Promise.all([
        getDocs(collection(db, 'cpsc_lab_mappings')),
        getDocs(collection(db, 'cpsc_citation_mappings')),
        getDocs(collection(db, 'cpsc_client_templates', tenantId, 'templates')),
      ]);
      setLabs(labSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => s(a.labLocation).localeCompare(s(b.labLocation))));
      setCitations(citeSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => s(a.standard).localeCompare(s(b.standard))));
      setTemplates(tplSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (Number(a.priority) || 99) - (Number(b.priority) || 99)));
    } catch (e) {
      // Surface, don't swallow (§8.3).
      setError(e?.message || 'Failed to load CPSC mappings.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  // Lab mapping CRUD via the existing audited manageCpscMapping callable (validates a 4-digit CPSC Lab ID).
  const manageLab = useCallback(async (operation, key, value) => {
    setNotice(null); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'manageCpscMapping');
      await fn({ operation, mappingType: 'lab', key, ...(value !== undefined ? { value } : {}) });
      setNotice(`Lab mapping ${operation === 'delete' ? 'deleted' : 'saved'}: ${key}${value ? ` → ${value}` : ''}.`);
      await load();
    } catch (e) {
      setError(e?.message || `Could not ${operation} the lab mapping.`);
      throw e; // let the row's busy state settle but keep the error visible
    }
  }, [load]);

  // CHA-203: per-client template CRUD via the audited manageCpscClientTemplate callable.
  const manageTemplate = useCallback(async (operation, payload) => {
    setNotice(null); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'manageCpscClientTemplate');
      await fn({ operation, ...payload, ...(userProfile?.tenantId ? {} : {}) });
      setNotice(`Client template ${operation === 'delete' ? 'deleted' : 'saved'}.`);
      await load();
    } catch (e) { setError(e?.message || `Could not ${operation} the template.`); throw e; }
  }, [load, userProfile]);

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Mappings is restricted to administrators.
      </div>
    );
  }

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-3xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink">CPSC Mappings</h3>
        <p className="text-sm text-carbon-gray-70">
          Global lookup tables that turn BV lab locations and test standards into CPSC Lab IDs and citation
          codes. Add or edit <strong>lab mappings</strong> below — the CPSC Lab ID is the 4-digit accredited-lab
          ID from the CPSC registry (e.g. Bureau Veritas Shanghai = 1018).
        </p>
      </div>

      {notice && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-1.5">{notice}</div>}
      {error && <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span></div>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-carbon-gray-70 px-1 py-6"><Loader2 size={15} className="animate-spin" /> Loading mappings…</div>
      ) : (
        <>
          <MappingTable
            title="Lab Mappings" icon={FlaskConical} rows={labs}
            keyCol="BV Lab Location" valCol="CPSC Lab ID" keyField="labLocation" valField="cpscLabId"
            empty="No lab mappings yet — add one below."
            manage={manageLab} keyPlaceholder="e.g. SHANGHAI" valPlaceholder="e.g. 1018"
            valHint="Find the 4-digit CPSC Lab ID in the CPSC accredited-lab registry; the BV Lab Location is the value in the report's 'Lab Location' column."
          />
          <MappingTable
            title="Citation Mappings" icon={FileText} rows={citations}
            keyCol="Standard" valCol="CPSC Citation Code" keyField="standard" valField="code"
            empty="No citation mappings yet — run the seed script."
          />
          <ClientTemplates rows={templates} manage={manageTemplate} />
        </>
      )}

      <div className="text-[11px] text-carbon-gray-50 flex items-start gap-1">
        <Lock size={11} className="mt-0.5 shrink-0" />
        <span>Global, CPSC-jurisdictional only. Lab + citation mappings are shared platform-wide; every write goes through the audited <code>manageCpscMapping</code> callable.</span>
      </div>
    </div>
  );
}
